import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useParams } from 'react-router-dom';
import { Flexbox, FlexboxColumn } from '../../components/action/Flexbox';
import { Line } from '../../components/action/Line';
import RedirectButton from '../../components/action/RedirectButton';
import SearchBar from '../../components/action/SearchBar';
import PositionAnalysis from '../../components/Analysis/PositionAnalysis';
import { BlueBadge } from '../../components/Badge/Badge';
import BreadcrumbNavigator from '../../components/BreadcrumbNavigator/BreadcrumbNavigator';
import ProjectInfo from '../../components/ProjectInfo/ProjectInfo';
import TableComponent from '../../components/TableContainer/TableContainer';
import ThemedText from '../../components/theme/ThemedText';
import ThemeLogo from '../../components/theme/ThemeLogo';
import UserReputation from '../../components/UserReputation/UserReputation';
import { CHAINS } from '../../constants';
import { useBackendApi } from '../../helpers/backendHelper';
import { useMrepState } from '../../helpers/mrepHelper';
import { useGlobalState } from '../../state/GlobalStateProvider';
import { filterObject, formatNumber, formatNumberWithCommas, formatTiers, trackClairtyEvent } from '../../utils';
import './Chain.scss';

const Chain = () => {
  const { chainId } = useParams();
  const [{ chainMapping }] = useGlobalState();
  const [{ chainRanking, userReputation, getActionStats }] = useMrepState();
  const [metadata, setMetadata] = useState();
  const { userAddress } = useParams();
  const [chainRankingData, setChainRankingData] = useState();
  const [chainActionStats, setChainActionStats] = useState();
  const [chainFilters, setChainFilters] = useState();
  const [protocolData, setProtocolData] = useState();
  const backendHelper = useBackendApi();
  const [searchTerm, setSearchTerm] = useState();

  async function getChainMetadata() {
    const metadata = await backendHelper.mrep.getChainMetadata(chainId);
    setMetadata(metadata?.data);
  }

  async function getIndividualChainRanking(userAddress) {
    try {
      const body = {
        address: userAddress,
        filter: {
          chainId: Number(chainId),
        },
      };
      const data = await backendHelper.mrep.getChainWiseMrepData(body);
      setChainRankingData(data?.data?.rankings?.[0]);
    } catch (error) {
      return null;
    }
  }

  async function getProtocolData(userAddress) {
    try {
      setProtocolData(null);
      const body = {
        address: userAddress,
        filter: {
          ...filterObject({
            chainId: Number(chainId),
            actionType: chainFilters?.actionType,
            timeDeltas: ['7d', '30d'],
          }),
        },
      };
      const data = await backendHelper.mrep.getProtocolWiseMrepData(body);

      setProtocolData(data?.data?.rankings || []);
    } catch (error) {
      setProtocolData([]);
    }
  }

  function handleFilters(key, value) {
    setChainFilters({ ...chainFilters, [key]: value });
  }

  useEffect(() => {
    if (chainId && userAddress) {
      getChainMetadata();
      getIndividualChainRanking(userAddress);
      getActionStats({ chainId: Number(chainId) }, userAddress).then(res => {
        setChainActionStats(res);
      });
      getProtocolData(userAddress);
      
      trackClairtyEvent('user_profile_details', {
        project: chainId,
      });
    }
  }, [chainId, userAddress]);

  useEffect(() => {
    if (chainFilters) {
      getProtocolData(userAddress);
    }
  }, [chainFilters]);

  return (
    <FlexboxColumn fw>
      <BreadcrumbNavigator isChain={true} />
      <FlexboxColumn fw>
        <Flexbox justifyContent='space-between' fw>
          <FlexboxColumn fw justifyContent='space-between' alignItems={'flex-start'}>
            <ProjectInfo project={CHAINS?.[chainId]} isChain={true} />
            {metadata ? (
              <Flexbox fw justifyContent='flex-start'>
                <ThemedText.H5>
                  Total users tracked<ThemedText.Span weight='semi-bold'>{formatNumber(metadata?.trackedUsers)}</ThemedText.Span>
                </ThemedText.H5>
                <ThemeLogo icon={'verticalLine'} />
                <Flexbox gap='0.5rem'>
                  <ThemedText.H5>Actions</ThemedText.H5>
                  <BlueBadge text={'All Actions'} />
                </Flexbox>
              </Flexbox>
            ) : (
              <Flexbox fw justifyContent='flex-start'>
                <Skeleton height={30} width={500} />
              </Flexbox>
            )}
          </FlexboxColumn>
          <Flexbox gap='0.5rem'>
            <RedirectButton url={CHAINS?.[chainId]?.url}>
              <ThemedText.H6>Visit App</ThemedText.H6>
            </RedirectButton>
            <ThemeLogo icon={'verticalLine'} />
            <RedirectButton url={CHAINS?.[chainId]?.twitterUrl}>
              <ThemeLogo icon={'twitterLogo'} />
            </RedirectButton>
          </Flexbox>
        </Flexbox>
        <Line />

        <Flexbox fw justifyContent='space-between'>
          <Flexbox gap='0.75rem'>
            <ThemeLogo icon={'cup'} />
            <FlexboxColumn gap='0.25rem' alignItems='flex-start'>
              <Flexbox>
                <ThemedText.H3 weight='semi-bold'>#{formatNumberWithCommas(chainRankingData?.rank)}</ThemedText.H3>{' '}
                {formatTiers(chainRankingData?.top) && <BlueBadge text={`Top ${formatTiers(chainRankingData?.top)}%`} />}
              </Flexbox>
              <ThemedText.H6>Overall Contribution Rank</ThemedText.H6>
            </FlexboxColumn>
          </Flexbox>
          <Flexbox gap='0.75rem'>
            <SearchBar placeholder='Search Projects' value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </Flexbox>
        </Flexbox>
        <UserReputation data={chainActionStats} filter={chainFilters?.actionType} onChange={value => handleFilters('actionType', value)} />
        <PositionAnalysis filters={{ chainFilter: { chainId: Number(chainId) } }} userAddress={userAddress} userData={chainRankingData} isChain={true} />
        <TableComponent data={protocolData} isLoading={!protocolData} searchTerm={searchTerm} />
      </FlexboxColumn>
    </FlexboxColumn>
  );
};

export default Chain;
